import {
  type MutableRefObject,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const DEFAULT_SCROLL_THRESHOLD = 400;
const UNPIN_DELTA_THRESHOLD = 5;
const SCROLL_ANIMATION_DURATION_MS = 800;

const easeOutCubic = (progress: number): number => 1 - (1 - progress) ** 3;

type AnimationDirection = 'up' | 'down' | null;

const animateScrollTo = (
  container: HTMLElement,
  targetScrollTop: number,
  rafIdRef: MutableRefObject<number | null>,
  animationDirectionRef: MutableRefObject<AnimationDirection>
): void => {
  if (rafIdRef.current !== null) {
    cancelAnimationFrame(rafIdRef.current);
    rafIdRef.current = null;
    animationDirectionRef.current = null;
  }

  const startScrollTop = container.scrollTop;
  const distance = targetScrollTop - startScrollTop;
  if (Math.abs(distance) < 1) {
    return;
  }

  animationDirectionRef.current = distance > 0 ? 'down' : 'up';
  const startTime = performance.now();

  const step = (currentTime: number) => {
    const elapsed = Math.max(0, currentTime - startTime);
    const progress = Math.min(elapsed / SCROLL_ANIMATION_DURATION_MS, 1);
    container.scrollTop = startScrollTop + distance * easeOutCubic(progress);

    if (progress < 1) {
      rafIdRef.current = requestAnimationFrame(step);
    } else {
      rafIdRef.current = null;
      animationDirectionRef.current = null;
    }
  };

  rafIdRef.current = requestAnimationFrame(step);
};

const computeScrollTarget = (
  container: HTMLElement,
  element: HTMLElement
): number => {
  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  const paddingTop = parseFloat(getComputedStyle(container).paddingTop) || 0;
  const marginTop = parseFloat(getComputedStyle(element).marginTop) || 0;

  return Math.max(
    0,
    container.scrollTop +
      (elementRect.top - containerRect.top) -
      paddingTop -
      marginTop
  );
};

interface ScrollRefs {
  isAtBottomRef: MutableRefObject<boolean>;
  pinnedToBottomRef: MutableRefObject<boolean>;
  lastScrollTopRef: MutableRefObject<number>;
  animationDirectionRef: MutableRefObject<AnimationDirection>;
}

interface AllScrollRefs extends ScrollRefs {
  containerRef: RefObject<HTMLDivElement>;
  rafIdRef: MutableRefObject<number | null>;
}

const scrollToBottomAction = (
  refs: AllScrollRefs,
  setIsAtBottom: (v: boolean) => void
) => {
  const container = refs.containerRef.current;
  if (!container) {
    return;
  }
  refs.pinnedToBottomRef.current = true;
  refs.isAtBottomRef.current = true;
  setIsAtBottom(true);
  animateScrollTo(
    container,
    container.scrollHeight,
    refs.rafIdRef,
    refs.animationDirectionRef
  );
};

const scrollToElementAction = (
  refs: AllScrollRefs,
  element: HTMLElement,
  setIsAtBottom: (v: boolean) => void
) => {
  const container = refs.containerRef.current;
  if (!container) {
    return;
  }
  refs.pinnedToBottomRef.current = false;
  refs.isAtBottomRef.current = false;
  setIsAtBottom(false);
  animateScrollTo(
    container,
    computeScrollTarget(container, element),
    refs.rafIdRef,
    refs.animationDirectionRef
  );
};

const createScrollHandler = (
  container: HTMLDivElement,
  refs: ScrollRefs,
  threshold: number,
  setIsAtBottom: (v: boolean) => void,
  rafIdRef: MutableRefObject<number | null>
) => {
  return () => {
    const currentScrollTop = container.scrollTop;
    const scrollDelta = currentScrollTop - refs.lastScrollTopRef.current;
    const scrollingUp = scrollDelta < -UNPIN_DELTA_THRESHOLD;
    const scrollingDown = scrollDelta > UNPIN_DELTA_THRESHOLD;
    const animationDirection = refs.animationDirectionRef.current;

    const cancelRunningAnimation = () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
        refs.animationDirectionRef.current = null;
      }
    };

    if (scrollingUp && animationDirection !== 'up') {
      cancelRunningAnimation();
      refs.pinnedToBottomRef.current = false;
      if (refs.isAtBottomRef.current) {
        const distanceFromBottom =
          container.scrollHeight - currentScrollTop - container.clientHeight;
        if (distanceFromBottom >= threshold) {
          refs.isAtBottomRef.current = false;
          setIsAtBottom(false);
        }
      }
    }

    if (scrollingDown && animationDirection !== 'down') {
      cancelRunningAnimation();
      const distanceFromBottom =
        container.scrollHeight - currentScrollTop - container.clientHeight;
      const nearBottom = distanceFromBottom < threshold;

      if (nearBottom && !refs.isAtBottomRef.current) {
        refs.isAtBottomRef.current = true;
        refs.pinnedToBottomRef.current = true;
        setIsAtBottom(true);
      }
    }

    if (scrollingUp || scrollingDown) {
      refs.lastScrollTopRef.current = currentScrollTop;
    }
  };
};

const createMutationHandler = (
  container: HTMLDivElement,
  refs: ScrollRefs,
  threshold: number,
  setIsAtBottom: (v: boolean) => void,
  rafIdRef: MutableRefObject<number | null>,
  mutationRafIds: Set<number>
) => {
  let pendingCheck = false;
  let pinnedScrollPending = false;

  const checkPosition = () => {
    pendingCheck = false;
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    const atBottom = distanceFromBottom < threshold;

    if (atBottom !== refs.isAtBottomRef.current) {
      refs.isAtBottomRef.current = atBottom;
      setIsAtBottom(atBottom);
    }
  };

  return () => {
    if (rafIdRef.current !== null) {
      return;
    }

    if (refs.pinnedToBottomRef.current) {
      if (!pinnedScrollPending) {
        pinnedScrollPending = true;
        const id = requestAnimationFrame(() => {
          mutationRafIds.delete(id);
          pinnedScrollPending = false;
          if (refs.pinnedToBottomRef.current && rafIdRef.current === null) {
            container.scrollTop = container.scrollHeight;
            if (!refs.isAtBottomRef.current) {
              refs.isAtBottomRef.current = true;
              setIsAtBottom(true);
            }
          }
        });
        mutationRafIds.add(id);
      }
    } else if (!pendingCheck) {
      pendingCheck = true;
      const id = requestAnimationFrame(() => {
        mutationRafIds.delete(id);
        checkPosition();
      });
      mutationRafIds.add(id);
    }
  };
};

const setupScrollObservers = (
  container: HTMLDivElement,
  refs: ScrollRefs,
  threshold: number,
  setIsAtBottom: (v: boolean) => void,
  rafIdRef: MutableRefObject<number | null>
) => {
  const handleScroll = createScrollHandler(
    container,
    refs,
    threshold,
    setIsAtBottom,
    rafIdRef
  );
  container.addEventListener('scroll', handleScroll, { passive: true });

  const mutationRafIds = new Set<number>();
  const handleMutation = createMutationHandler(
    container,
    refs,
    threshold,
    setIsAtBottom,
    rafIdRef,
    mutationRafIds
  );
  const mutationObserver = new MutationObserver(handleMutation);
  mutationObserver.observe(container, {
    childList: true,
    subtree: true,
    characterData: true,
  });

  return () => {
    container.removeEventListener('scroll', handleScroll);
    mutationObserver.disconnect();
    mutationRafIds.forEach((id) => cancelAnimationFrame(id));
    mutationRafIds.clear();
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
      refs.animationDirectionRef.current = null;
    }
  };
};

export const useTrackScrollToBottom = (
  threshold = DEFAULT_SCROLL_THRESHOLD
) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);
  const pinnedToBottomRef = useRef(false);
  const lastScrollTopRef = useRef(0);
  const rafIdRef = useRef<number | null>(null);
  const animationDirectionRef = useRef<AnimationDirection>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);

  const refs = useMemo<AllScrollRefs>(
    () => ({
      containerRef,
      isAtBottomRef,
      pinnedToBottomRef,
      lastScrollTopRef,
      rafIdRef,
      animationDirectionRef,
    }),
    []
  );

  const scrollToBottom = useCallback(
    () => scrollToBottomAction(refs, setIsAtBottom),
    [refs]
  );

  const scrollToElement = useCallback(
    (element: HTMLElement) =>
      scrollToElementAction(refs, element, setIsAtBottom),
    [refs]
  );

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }
    return setupScrollObservers(
      containerRef.current,
      refs,
      threshold,
      setIsAtBottom,
      rafIdRef
    );
  }, [threshold, refs]);

  return {
    containerRef,
    isAtBottomRef,
    isAtBottom,
    scrollToBottom,
    scrollToElement,
  };
};
